/**
 * 認証共通ライブラリ
 * ログインリダイレクト、トークン更新、コールバック処理、認証状態確認、ログアウト、パスワードリセット
 */

const { info, error: logError, warn } = require('./logging');
const { handleApiError } = require('./error-handling');

/**
 * API Gateway URL
 * ログイン要求APIのエンドポイントURL
 */
// ビルド時に GitHub Secrets から注入される想定。
// 未設定の場合は明示的にエラーを投げて気付けるようにする。
// ビルド時に process.env.API_GATEWAY_URL が文字列リテラルに置き換えられる
const API_GATEWAY_URL = process.env.API_GATEWAY_URL || '';
if (!API_GATEWAY_URL) {
  throw new Error('API_GATEWAY_URL is not defined. Set it via build-time environment (e.g., GitHub Secrets).');
}

/**
 * ランダムなstate文字列を生成（64文字のhex文字列）
 * 32バイトのランダムデータを生成し、hexエンコードして64文字の文字列を生成
 * @returns {string} 64文字のランダムなhex文字列
 */
function generateState() {
  // ブラウザ環境でのランダムバイト生成
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    // 32バイト（256ビット）のランダムデータを生成
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    // hex文字列に変換（64文字）
    return Array.from(array)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Node.js環境でのランダムバイト生成
  if (typeof require !== 'undefined') {
    const crypto = require('crypto');
    return crypto.randomBytes(32).toString('hex');
  }

  // フォールバック（推奨されないが、互換性のため）
  throw new Error('Crypto API not available');
}

/**
 * clientIdのバリデーション
 * @param {string} clientId - 検証するclientId
 * @returns {Object|null} バリデーション結果、エラーの場合はエラー情報を返す
 */
function validateClientId(clientId) {
  // 空文字列でないことを確認
  if (!clientId || typeof clientId !== 'string' || clientId.trim().length === 0) {
    return {
      isValid: false,
      errorCode: 'E004-0002',
      errorMessage: '必須パラメータが不足しています（clientId）',
      statusCode: 400
    };
  }

  // Auth0のClient ID形式の簡易チェック（英数字とハイフン、アンダースコアが使用可能）
  // 実際の形式はAuth0が発行するため、ここでは基本的な形式チェックのみ
  if (!/^[a-zA-Z0-9_-]+$/.test(clientId)) {
    return {
      isValid: false,
      errorCode: 'E004-0001',
      errorMessage: 'パラメータが不正です（clientId）',
      statusCode: 400
    };
  }

  return { isValid: true };
}

/**
 * redirectUriのバリデーション
 * @param {string} redirectUri - 検証するredirectUri
 * @returns {Object|null} バリデーション結果、エラーの場合はエラー情報を返す
 */
function validateRedirectUri(redirectUri) {
  try {
    const url = new URL(redirectUri);
    // http/httpsスキームが必須
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return {
        isValid: false,
        errorCode: 'E004-0001',
        errorMessage: 'パラメータが不正です（redirectUri）',
        statusCode: 400
      };
    }
    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      errorCode: 'E004-0001',
      errorMessage: 'パラメータが不正です（redirectUri）',
      statusCode: 400
    };
  }
}

/**
 * stateのバリデーション
 * CSRF対策用のランダム文字列（任意の文字列、推奨：64文字以上のランダム文字列）
 * 形式チェック（文字数制限等）のみを実施
 * @param {string} state - 検証するstate
 * @returns {Object|null} バリデーション結果、エラーの場合はエラー情報を返す
 */
function validateState(state) {
  // 形式チェック（文字数制限等）：64文字以上であること
  if (!state || typeof state !== 'string' || state.length < 64) {
    return {
      isValid: false,
      errorCode: 'E004-0001',
      errorMessage: 'パラメータが不正です（state）',
      statusCode: 400
    };
  }

  return { isValid: true };
}

/**
 * 認可コードのバリデーション
 * Auth0の認可コード形式をチェック（Base64URL形式：英数字、ハイフン、アンダースコア、ピリオド）
 * @param {string} code - 検証する認可コード
 * @returns {Object|null} バリデーション結果、エラーの場合はエラー情報を返す
 */
function validateAuthorizationCode(code) {
  // 空文字列でないことを確認
  if (!code || typeof code !== 'string' || code.trim().length === 0) {
    return {
      isValid: false,
      errorCode: 'E004-0002',
      errorMessage: '必須パラメータが不足しています（code）',
      statusCode: 400
    };
  }

  // 認可コードはBase64URL形式（英数字、ハイフン、アンダースコア、ピリオド）
  // 最小長さは10文字以上（Auth0の認可コードは通常20文字以上だが、柔軟性を持たせる）
  // 最大長さは512文字（OAuth2仕様の推奨値）
  if (code.length < 10 || code.length > 512) {
    return {
      isValid: false,
      errorCode: 'E004-0001',
      errorMessage: 'パラメータが不正です（code）',
      statusCode: 400
    };
  }

  // Base64URL形式の文字のみを許可（A-Z, a-z, 0-9, -, _, .）
  const authorizationCodePattern = /^[A-Za-z0-9\-_.]+$/;
  if (!authorizationCodePattern.test(code)) {
    return {
      isValid: false,
      errorCode: 'E004-0001',
      errorMessage: 'パラメータが不正です（code）',
      statusCode: 400
    };
  }

  return { isValid: true };
}

/**
 * JWTトークンをデコード（署名検証なし、expクレーム取得用）
 * @param {string} token - JWTトークン
 * @returns {Object|null} デコードされたペイロード、失敗時はnull
 */
function decodeJwtToken(token) {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    // JWTは3つの部分に分かれている: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // ペイロード部分をデコード（Base64URLデコード）
    const payload = parts[1];

    // Base64URLデコード（ブラウザ環境）
    if (typeof window !== 'undefined') {
      // Base64URLをBase64に変換（- を + に、_ を / に）
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      // パディングを追加
      const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
      // Base64デコード
      const decoded = atob(padded);
      // JSONパース
      return JSON.parse(decoded);
    }

    // Node.js環境
    if (typeof Buffer !== 'undefined') {
      // Base64URLデコード
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
      const decoded = Buffer.from(padded, 'base64').toString('utf8');
      return JSON.parse(decoded);
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * トークンの有効期限を確認
 * @param {string} token - JWTトークン
 * @returns {boolean} 有効期限内の場合true、期限切れまたは無効な場合false
 */
function isTokenValid(token) {
  const decoded = decodeJwtToken(token);
  if (!decoded || !decoded.exp) {
    return false;
  }

  // expはUnix時間（秒）なので、現在時刻（秒）と比較
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp > currentTime;
}

/**
 * ログインリダイレクト
 * Auth0 Universal Login URLを生成する
 * 
 * @param {string} clientId - システム識別子（Auth0のClient ID）
 * @param {string} [redirectUri] - コールバックURL（任意）
 * @param {Object} [options] - 追加オプション
 * @param {string} [options.state] - CSRF対策用のランダム文字列（任意、推奨：64文字以上のランダム文字列）
 * @returns {Promise<string>} Auth0 Universal Login URL
 * @throws {Error} バリデーションエラーまたはAPI呼び出しエラー
 */
async function redirectToLogin(clientId, redirectUri, options = {}) {
  // 1. パラメータの取得と基本バリデーション

  // clientIdのバリデーション
  const clientIdValidation = validateClientId(clientId);
  if (!clientIdValidation.isValid) {
    const error = new Error(`${clientIdValidation.errorCode}: ${clientIdValidation.errorMessage}`);
    error.code = clientIdValidation.errorCode;
    error.statusCode = clientIdValidation.statusCode;

    // エラーログ出力
    logError('ログインリダイレクト処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'GET',
        path: '/api/auth/login',
        status: clientIdValidation.statusCode
      },
      data: {
        message_id: clientIdValidation.errorCode,
        clientId: clientId,
        redirectUri: redirectUri || null
      }
    });

    throw error;
  }

  // redirectUriのバリデーション（設定されている場合のみ）
  if (redirectUri !== undefined && redirectUri !== null) {
    const redirectUriValidation = validateRedirectUri(redirectUri);
    if (!redirectUriValidation.isValid) {
      const error = new Error(`${redirectUriValidation.errorCode}: ${redirectUriValidation.errorMessage}`);
      error.code = redirectUriValidation.errorCode;
      error.statusCode = redirectUriValidation.statusCode;

      // エラーログ出力
      logError('ログインリダイレクト処理でバリデーションエラーが発生しました', {
        event: 'error',
        category: 'validation',
        http: {
          method: 'GET',
          path: '/api/auth/login',
          status: redirectUriValidation.statusCode
        },
        data: {
          message_id: redirectUriValidation.errorCode,
          clientId: clientId,
          redirectUri: redirectUri
        }
      });

      throw error;
    }
  }

  // stateの取得または生成
  let state = options.state;
  if (state === undefined || state === null) {
    // options.stateが設定されていない場合、ランダム文字列を生成
    state = generateState();
  } else {
    // stateが設定されている場合、バリデーション（形式チェックのみ）
    const stateValidation = validateState(state);
    if (!stateValidation.isValid) {
      const error = new Error(`${stateValidation.errorCode}: ${stateValidation.errorMessage}`);
      error.code = stateValidation.errorCode;
      error.statusCode = stateValidation.statusCode;

      // エラーログ出力
      logError('ログインリダイレクト処理でバリデーションエラーが発生しました', {
        event: 'error',
        category: 'validation',
        http: {
          method: 'GET',
          path: '/api/auth/login',
          status: stateValidation.statusCode
        },
        data: {
          message_id: stateValidation.errorCode,
          clientId: clientId,
          redirectUri: redirectUri || null
        }
      });

      throw error;
    }
  }

  // 2. ログイン要求APIの呼び出し
  const queryParams = new URLSearchParams({
    client_id: clientId,
    state: state
  });

  if (redirectUri) {
    queryParams.append('redirect_uri', redirectUri);
  }

  const apiUrl = `${API_GATEWAY_URL}/api/auth/login?${queryParams.toString()}`;

  let response;
  try {
    response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  } catch (networkError) {
    // ネットワークエラー
    const error = new Error('E006-0004: Auth0 Management APIのタイムアウトエラー');
    error.code = 'E006-0004';
    error.statusCode = 504;
    error.originalError = networkError;

    // エラーログ出力
    logError('ログインリダイレクト処理でネットワークエラーが発生しました', {
      event: 'error',
      category: 'network',
      http: {
        method: 'GET',
        path: '/api/auth/login',
        status: 504
      },
      data: {
        message_id: 'E006-0004',
        clientId: clientId,
        redirectUri: redirectUri || null
      }
    });

    throw error;
  }

  // レスポンスの解析
  let responseData;
  try {
    responseData = await response.json();
  } catch (parseError) {
    const error = new Error('E005-0004: Auth0 Management APIの取得エラー等');
    error.code = 'E005-0004';
    error.statusCode = 500;
    error.originalError = parseError;

    // エラーログ出力
    logError('ログインリダイレクト処理でレスポンス解析エラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/auth/login',
        status: 500
      },
      data: {
        message_id: 'E005-0004',
        clientId: clientId,
        redirectUri: redirectUri || null
      }
    });

    throw error;
  }

  // エラーレスポンスの処理
  if (!response.ok) {
    const apiError = handleApiError(response, responseData, 'Auth0 Management APIの取得エラー等');

    // エラーログ出力
    logError('ログインリダイレクト処理でAPIエラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/auth/login',
        status: apiError.statusCode
      },
      data: {
        message_id: apiError.code || 'E005-0004',
        clientId: clientId,
        redirectUri: redirectUri || null
      }
    });

    throw apiError;
  }

  // 3. レスポンスからauth_urlを取得
  if (!responseData.data || !responseData.data.auth_url) {
    const error = new Error('E005-0004: Auth0 Management APIの取得エラー等');
    error.code = 'E005-0004';
    error.statusCode = 500;

    // エラーログ出力
    logError('ログインリダイレクト処理でレスポンスデータが不正です', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/auth/login',
        status: 500
      },
      data: {
        message_id: 'E005-0004',
        clientId: clientId,
        redirectUri: redirectUri || null
      }
    });

    throw error;
  }

  const authUrl = responseData.data.auth_url;

  // 4. auth_urlを返却
  // アクセスログ出力
  info('ログインリダイレクト処理が正常に完了しました', {
    event: 'login_redirect_success',
    category: 'auth',
    http: {
      method: 'GET',
      path: '/api/auth/login',
      status: 200
    },
    data: {
      clientId: clientId,
      redirectUri: redirectUri || null
    }
  });

  return authUrl;
}

/**
 * コールバック処理
 * Auth0認証完了後のコールバックを統一処理する
 * 
 * @param {string} code - Auth0から返される認可コード
 * @param {string} state - CSRF対策用のstate
 * @returns {Promise<Object>} 認証結果（accessToken, idToken, refreshToken, expiresIn, user）
 * @throws {Error} バリデーションエラーまたはAPI呼び出しエラー
 */
async function handleCallback(code, state) {
  // 1. パラメータのバリデーション

  // codeのバリデーション
  const codeValidation = validateAuthorizationCode(code);
  if (!codeValidation.isValid) {
    const error = new Error(`${codeValidation.errorCode}: ${codeValidation.errorMessage}`);
    error.code = codeValidation.errorCode;
    error.statusCode = codeValidation.statusCode;

    // エラーログ出力
    logError('コールバック処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'POST',
        path: '/api/auth/callback',
        status: codeValidation.statusCode
      },
      data: {
        message_id: codeValidation.errorCode
      }
    });

    throw error;
  }

  // stateの必須チェック
  if (!state || typeof state !== 'string' || state.trim().length === 0) {
    const error = new Error('E004-0002: 必須パラメータが不足しています（state）');
    error.code = 'E004-0002';
    error.statusCode = 400;

    // エラーログ出力
    logError('コールバック処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'POST',
        path: '/api/auth/callback',
        status: 400
      },
      data: {
        message_id: 'E004-0002'
      }
    });

    throw error;
  }

  // 2. 必要な情報を取得

  // API Gateway URLの取得
  // 環境変数または設定から取得（現在は定数を使用）
  const apiGatewayUrl = API_GATEWAY_URL;
  if (!apiGatewayUrl) {
    const error = new Error('E005-0003: 環境変数が設定されていない');
    error.code = 'E005-0003';
    error.statusCode = 500;

    // エラーログ出力
    logError('コールバック処理で環境変数エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'POST',
        path: '/api/auth/callback',
        status: 500
      },
      data: {
        message_id: 'E005-0003'
      }
    });

    throw error;
  }

  // stateからclient_idを抽出（署名付きstate形式: {nonce}.{timestamp}.{clientId}.{signature}）
  const parts = state.split('.');
  const clientId = parts.length >= 3 ? parts[2] : null;
  if (!clientId) {
    const error = new Error('E004-0001: stateパラメータの形式が不正です');
    error.code = 'E004-0001';
    error.statusCode = 400;

    // エラーログ出力
    logError('コールバック処理でstate形式エラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'POST',
        path: '/api/auth/callback',
        status: 400
      },
      data: {
        message_id: 'E004-0001'
      }
    });

    throw error;
  }

  // redirect_uriの取得（現在のURLから取得、URLパラメータを除去）
  if (typeof window === 'undefined' || !window.location) {
    const error = new Error('E005-0003: ブラウザ環境で実行する必要があります');
    error.code = 'E005-0003';
    error.statusCode = 500;

    // エラーログ出力
    logError('コールバック処理で環境エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'POST',
        path: '/api/auth/callback',
        status: 500
      },
      data: {
        message_id: 'E005-0003',
        client_id: clientId
      }
    });

    throw error;
  }

  const redirectUri = window.location.origin + window.location.pathname;

  // 4. バックエンドAPI（POST /api/auth/callback）を呼び出し
  const apiUrl = `${apiGatewayUrl}/api/auth/callback`;

  let response;
  try {
    response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include', // Cookieを受け取るために必要（クロスサイトリクエストでSet-Cookieヘッダーを受け取るため）
      body: JSON.stringify({
        code: code,
        state: state,
        client_id: clientId,
        redirect_uri: redirectUri
      })
    });
  } catch (networkError) {
    // ネットワークエラー
    const error = new Error('E006-0001: Auth0 エラーが発生しました');
    error.code = 'E006-0001';
    error.statusCode = 500;
    error.originalError = networkError;

    // エラーログ出力
    logError('コールバック処理でネットワークエラーが発生しました', {
      event: 'error',
      category: 'network',
      http: {
        method: 'POST',
        path: '/api/auth/callback',
        status: 500
      },
      data: {
        message_id: 'E006-0001',
        client_id: clientId
      }
    });

    throw error;
  }

  // レスポンスの解析
  let responseData;
  try {
    responseData = await response.json();
  } catch (parseError) {
    const error = new Error('E006-0001: Auth0 エラーが発生しました');
    error.code = 'E006-0001';
    error.statusCode = 500;
    error.originalError = parseError;

    // エラーログ出力
    logError('コールバック処理でレスポンス解析エラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'POST',
        path: '/api/auth/callback',
        status: 500
      },
      data: {
        message_id: 'E006-0001',
        client_id: clientId
      }
    });

    throw error;
  }

  // エラーレスポンスの処理
  if (!response.ok) {
    const apiError = handleApiError(response, responseData, 'Auth0 Management APIの取得エラー等');

    // エラーログ出力
    logError('コールバック処理でAPIエラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'POST',
        path: '/api/auth/callback',
        status: apiError.statusCode
      },
      data: {
        message_id: apiError.code || 'E006-0001',
        client_id: clientId
      }
    });

    throw apiError;
  }

  // 5. トークンはサーバー側でSet-CookieヘッダーによりHttpOnly Cookieに保存される
  // クライアント側では、コールバック処理APIを呼び出すのみ
  // Set-Cookieヘッダーで設定されたCookieが自動的に保存される

  // アクセスログ出力
  info('コールバック処理が正常に完了しました', {
    event: 'callback_success',
    category: 'auth',
    http: {
      method: 'POST',
      path: '/api/auth/callback',
      status: 200
    },
    data: {}
  });

  // 6. 処理完了（戻り値なし）
  return;
}

/**
 * 認証チェックAPI呼び出しの共通関数
 * @param {string} apiUrl - 認証チェックAPIのURL
 * @returns {Promise<Object|null>} レスポンスデータ（エラーの場合はnull）
 */
async function callAuthCheckApi(apiUrl) {
  let response;
  try {
    response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include' // Cookieを送信するために必要
    });
  } catch (networkError) {
    // ネットワークエラー
    logError('認証チェック処理でネットワークエラーが発生しました', {
      event: 'error',
      category: 'network',
      http: {
        method: 'GET',
        path: '/api/auth/check',
        status: 500
      },
      data: {
        message_id: 'E005-0004'
      }
    });

    return null;
  }

  // レスポンスの解析
  let responseData;
  try {
    // レスポンスボディが空の場合でもエラーにならないように、テキストとして取得してからJSON解析を試行
    const responseText = await response.text();
    if (responseText && responseText.trim().length > 0) {
      responseData = JSON.parse(responseText);
    } else {
      // レスポンスボディが空の場合、空オブジェクトを設定
      responseData = {};
    }
  } catch (parseError) {
    // エラーログ出力
    logError('認証チェック処理でレスポンス解析エラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/auth/check',
        status: response.status || 500
      },
      data: {
        message_id: 'E005-0004',
        error: parseError.message
      }
    });

    // レスポンスボディが解析できない場合でも、レスポンスオブジェクト自体は返す
    // これにより、HTTPステータスコードからエラーコードを推測できる
    responseData = {};
  }

  return {
    response,
    responseData
  };
}

/**
 * レスポンスデータから認証結果を抽出・検証する
 * @param {Object} responseData - APIレスポンスデータ
 * @param {boolean} tokenRefreshed - トークンが更新されたかどうか
 * @returns {Object|null} 認証結果（検証失敗時はnull）
 */
function extractAuthResult(responseData, tokenRefreshed = false) {
  if (!responseData || !responseData.data) {
    return null;
  }

  const data = responseData.data;

  // ユーザー情報の検証
  let user = null;
  if (data.user) {
    const userData = data.user;
    // 必須フィールドの検証
    if (
      (typeof userData.originalUserId === 'number' ||
        (typeof userData.originalUserId === 'string' && userData.originalUserId.length > 0)) &&
      typeof userData.email === 'string' &&
      userData.email.length > 0 &&
      typeof userData.name === 'string' &&
      userData.name.length > 0
    ) {
      // originalUserIdを数値に変換（文字列の場合に対応）
      const originalUserId = typeof userData.originalUserId === 'number'
        ? userData.originalUserId
        : parseInt(userData.originalUserId, 10);

      if (!isNaN(originalUserId)) {
        user = {
          originalUserId: originalUserId,
          email: userData.email,
          name: userData.name
        };
      }
    }
  }

  // expiresAtの検証
  const expiresAt = (typeof data.expiresAt === 'number' && data.expiresAt > 0)
    ? data.expiresAt
    : null;

  // tokenIssuedAtの取得
  const tokenIssuedAt = (typeof data.tokenIssuedAt === 'number' && data.tokenIssuedAt > 0)
    ? data.tokenIssuedAt
    : null;

  return {
    isAuthenticated: data.isAuthenticated === true,
    user: user,
    expiresAt: expiresAt,
    tokenIssuedAt: tokenIssuedAt,
    tokenRefreshed: tokenRefreshed
  };
}

/**
 * 認証状態確認
 * 現在の認証状態を確認し、必要に応じて自動的にトークンを更新する
 * 
 * 設計ドキュメント: 06_06_認証チェック処理詳細設計.md
 * 
 * @returns {Promise<Object>} 認証結果（isAuthenticated, user, expiresAt, tokenRefreshed）
 * @throws {Error} バリデーションエラーまたはAPI呼び出しエラー（ただし、トークンが無効な場合はエラーをthrowせず、isAuthenticated: falseを返却）
 */
async function checkAuthStatus(clientId) {
  // ブラウザ環境チェック
  if (typeof window === 'undefined') {
    // ブラウザ環境でない場合、未認証として返却
    return {
      isAuthenticated: false,
      user: null,
      expiresAt: null,
      tokenRefreshed: false
    };
  }

  // API Gateway URLの取得
  const apiGatewayUrl = API_GATEWAY_URL;
  if (!apiGatewayUrl) {
    // 環境変数が設定されていない場合、未認証として返却
    return {
      isAuthenticated: false,
      user: null,
      expiresAt: null,
      tokenRefreshed: false
    };
  }

  // 1. 認証チェックAPI（GET /api/auth/check）を呼び出し
  // 注意: HttpOnly Cookieはブラウザが自動的に送信するため、Authorizationヘッダーは不要
  // 注意: HttpOnly CookieはJavaScriptからアクセスできないため、先にAPIを呼び出して
  //       エラーコードE001-0002（トークン有効期限切れ）が返された場合にトークン更新を試行する
  const checkApiUrl = `${apiGatewayUrl}/api/auth/check`;

  const checkResult = await callAuthCheckApi(checkApiUrl);
  if (!checkResult) {
    // ネットワークエラーまたはレスポンス解析エラーの場合、未認証として返却
    return {
      isAuthenticated: false,
      user: null,
      expiresAt: null,
      tokenRefreshed: false
    };
  }

  const { response, responseData } = checkResult;

  // 2. 認証チェックAPIが成功した場合
  if (response.ok) {
    const authResult = extractAuthResult(responseData, false);
    if (authResult) {
      // ユーザーIDを生成（ログ用）
      const userId = authResult.user
        ? authResult.user.originalUserId
        : null;

      // アクセスログ出力
      info('認証チェック処理が正常に完了しました', {
        event: 'auth_check_success',
        category: 'auth',
        http: {
          method: 'GET',
          path: '/api/auth/check',
          status: 200
        },
        data: {
          user_id: userId,
          token_refreshed: false
        }
      });

      // extractAuthResult関数がtokenRefreshedを含めて返すため、そのまま返却
      return authResult;
    } else {
      // レスポンスデータの検証に失敗した場合
      logError('認証チェック処理でレスポンスデータの検証に失敗しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'GET',
          path: '/api/auth/check',
          status: 200
        },
        data: {
          message_id: 'E005-0004'
        }
      });

      return {
        isAuthenticated: false,
        user: null,
        expiresAt: null,
        tokenRefreshed: false
      };
    }
  }

  // 3. 認証チェックAPIが失敗した場合（トークン有効期限切れ等）
  // エラーコードはレスポンスボディのみを信頼する（HTTPステータスから推測しない）
  // 1. responseData.error.code（標準的なエラーレスポンス形式）
  // 2. responseData.data.message_id（旧形式のエラーレスポンス形式）
  // Authorizer拒否（403 + codeなし）は後続の response.status 判定で refresh を試行する
  const errorCode = responseData?.error?.code || responseData?.data?.message_id;

  // アカウントブロック（E001-0003）の場合は、トークン更新を試行せず、そのままエラーとして返す
  // ※ボディに明示された場合のみ。Authorizer 403 をブロック扱いしてはならない
  if (errorCode === 'E001-0003') {
    // APIレスポンスからエラーメッセージを取得（なければデフォルトメッセージを使用）
    const errorMessage = responseData?.error?.message || 'アカウントがブロックされています';
    
    logError(errorMessage, {
      event: 'error',
      category: 'auth',
      http: {
        method: 'GET',
        path: '/api/auth/check',
        status: response.status
      },
      data: {
        message_id: errorCode
      }
    });

    return {
      isAuthenticated: false,
      user: null,
      expiresAt: null,
      tokenRefreshed: false
    };
  }

  // アクセストークン不足/有効期限切れ/権限エラーの場合、トークン更新を試行
  // - E001-0002: トークン有効期限切れ
  // - E001-0001: Cookie削除などでaccess_tokenが欠損
  // - E005-0004 or 401/403: トークン検証失敗や認証ヘッダー不足などでAPIが拒否したケース
  // 注意: E001-0003（アカウントブロック）は上記で処理済みのため、ここでは除外
  if (
    errorCode === 'E001-0002' ||
    errorCode === 'E001-0001' ||
    errorCode === 'E005-0004' ||
    response.status === 401 ||
    (response.status === 403 && errorCode !== 'E001-0003')
  ) {
    // 4. リフレッシュトークンの有効期限を確認するため、トークン更新APIを呼び出し
    // client_idの取得（ローカルストレージから）
    if (!window.localStorage) {
      // ローカルストレージが利用できない場合、未認証として返却
      return {
        isAuthenticated: false,
        user: null,
        expiresAt: null,
        tokenRefreshed: false
      };
    }


    // 5. トークン更新API（POST /api/auth/refresh）を呼び出し
    // 注意: refresh_tokenはCookieから取得されるため、リクエストボディには含めない
    const refreshApiUrl = `${apiGatewayUrl}/api/auth/refresh`;

    let refreshResponse;
    try {
      refreshResponse = await fetch(refreshApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Cookieを送信するために必要
        body: JSON.stringify({
          client_id: clientId
        })
      });
    } catch (networkError) {
      // ネットワークエラー
      logError('認証チェック処理でトークン更新APIのネットワークエラーが発生しました', {
        event: 'error',
        category: 'network',
        http: {
          method: 'POST',
          path: '/api/auth/refresh',
          status: 500
        },
        data: {
          message_id: 'E005-0004'
        }
      });

      return {
        isAuthenticated: false,
        user: null,
        expiresAt: null,
        tokenRefreshed: false
      };
    }

    // レスポンスの解析
    let refreshResponseData;
    try {
      refreshResponseData = await refreshResponse.json();
    } catch (parseError) {
      // エラーログ出力
      logError('認証チェック処理でトークン更新APIのレスポンス解析エラーが発生しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'POST',
          path: '/api/auth/refresh',
          status: 500
        },
        data: {
          message_id: 'E005-0004'
        }
      });

      return {
        isAuthenticated: false,
        user: null,
        expiresAt: null,
        tokenRefreshed: false
      };
    }

    // 6. トークン更新APIが成功した場合
    if (refreshResponse.ok) {
      // 新しいトークンはSet-Cookieヘッダーで設定される（クライアント側での保存処理は不要）

      // 7. 認証チェックAPI（GET /api/auth/check）を再度呼び出し
      const checkApiUrl = `${apiGatewayUrl}/api/auth/check`;
      const checkResultAfterRefresh = await callAuthCheckApi(checkApiUrl);
      if (!checkResultAfterRefresh) {
        // ネットワークエラーまたはレスポンス解析エラーの場合、未認証として返却
        return {
          isAuthenticated: false,
          user: null,
          expiresAt: null,
          tokenRefreshed: false
        };
      }

      const { response: checkResponse, responseData: checkResponseData } = checkResultAfterRefresh;

      // 8. 認証状態、ユーザー情報、有効期限を返却（tokenRefreshed: true）
      if (checkResponse.ok) {
        const authResult = extractAuthResult(checkResponseData, true);
        if (authResult) {
          // ユーザーIDを生成（ログ用）
          const userId = authResult.user
            ? authResult.user.originalUserId
            : null;

          // アクセスログ出力
          info('認証チェック処理が正常に完了しました（トークン更新あり）', {
            event: 'auth_check_success',
            category: 'auth',
            http: {
              method: 'GET',
              path: '/api/auth/check',
              status: 200
            },
            data: {
              user_id: userId,
              token_refreshed: true
            }
          });

          return authResult;
        } else {
          // レスポンスデータの検証に失敗した場合
          logError('認証チェック処理でレスポンスデータの検証に失敗しました', {
            event: 'error',
            category: 'api',
            http: {
              method: 'GET',
              path: '/api/auth/check',
              status: 200
            },
            data: {
              message_id: 'E005-0004'
            }
          });

          return {
            isAuthenticated: false,
            user: null,
            expiresAt: null,
            tokenRefreshed: false
          };
        }
      } else {
        // 認証チェックAPIが失敗した場合、未認証として返却
        const checkErrorCode = checkResponseData?.error?.code || checkResponseData?.data?.message_id || 'E005-0004';
        logError('認証チェック処理で認証チェックAPIエラーが発生しました', {
          event: 'error',
          category: 'api',
          http: {
            method: 'GET',
            path: '/api/auth/check',
            status: checkResponse.status
          },
          data: {
            message_id: checkErrorCode
          }
        });

        return {
          isAuthenticated: false,
          user: null,
          expiresAt: null,
          tokenRefreshed: false
        };
      }
    } else {
      // トークン更新APIが失敗した場合（リフレッシュトークンが無効等）
      // エラーログ出力
      const refreshErrorCode = refreshResponseData?.error?.code || refreshResponseData?.data?.message_id || 'E005-0004';
      logError('認証チェック処理でトークン更新APIエラーが発生しました', {
        event: 'error',
        category: 'api',
        http: {
          method: 'POST',
          path: '/api/auth/refresh',
          status: refreshResponse.status
        },
        data: {
          message_id: refreshErrorCode
        }
      });

      // 認証状態を未認証として返却
      return {
        isAuthenticated: false,
        user: null,
        expiresAt: null,
        tokenRefreshed: false
      };
    }
  } else {
    // その他のエラーの場合、未認証として返却
    logError('認証チェック処理でAPIエラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/auth/check',
        status: response.status
      },
      data: {
        message_id: errorCode || 'E005-0004'
      }
    });

    return {
      isAuthenticated: false,
      user: null,
      expiresAt: null,
      tokenRefreshed: false
    };
  }
}

/**
 * MFA登録チケット（Guardian Enrollment Ticket）を取得し、ticket_url を返す。
 * 呼び出し元で window.location.href = ticket_url とすればMFA設定画面を表示できる。
 *
 * @param {Object} [options] - オプション
 * @param {number} [options.ticket_lifetime_in_seconds] - チケットの有効期限（秒）
 * @returns {Promise<{ ticket_url: string }>} ticket_url（MFA設定画面のURL）
 * @throws {Error} バリデーションエラーまたはAPI呼び出しエラー
 */
async function getMfaEnrollmentTicket(options = {}) {
  const apiGatewayUrl = API_GATEWAY_URL;
  if (!apiGatewayUrl) {
    const error = new Error('E005-0003: 環境変数が設定されていない');
    error.code = 'E005-0003';
    error.statusCode = 500;
    logError('MFA登録チケット取得で環境変数エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: { method: 'POST', path: '/api/auth/mfa/enrollment-ticket', status: 500 },
      data: { message_id: 'E005-0003' }
    });
    throw error;
  }

  const body = {};
  if (options.ticket_lifetime_in_seconds != null && typeof options.ticket_lifetime_in_seconds === 'number' && options.ticket_lifetime_in_seconds > 0) {
    // API の上限は 30 日（2592000 秒）。超える場合は上限にクランプする
    const MAX_TICKET_LIFETIME = 2592000;
    body.ticket_lifetime_in_seconds = Math.min(options.ticket_lifetime_in_seconds, MAX_TICKET_LIFETIME);
  }

  let response;
  try {
    response = await fetch(`${apiGatewayUrl}/api/auth/mfa/enrollment-ticket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });
  } catch (networkError) {
    const error = new Error('E006-0004: ネットワークエラーが発生しました');
    error.code = 'E006-0004';
    error.statusCode = 504;
    error.originalError = networkError;
    logError('MFA登録チケット取得でネットワークエラーが発生しました', {
      event: 'error',
      category: 'network',
      http: { method: 'POST', path: '/api/auth/mfa/enrollment-ticket', status: 504 },
      data: { message_id: 'E006-0004' }
    });
    throw error;
  }

  let responseData;
  try {
    responseData = await response.json();
  } catch (parseError) {
    const error = new Error('E005-0004: レスポンス解析エラーが発生しました');
    error.code = 'E005-0004';
    error.statusCode = 500;
    error.originalError = parseError;
    logError('MFA登録チケット取得でレスポンス解析エラーが発生しました', {
      event: 'error',
      category: 'api',
      http: { method: 'POST', path: '/api/auth/mfa/enrollment-ticket', status: 500 },
      data: { message_id: 'E005-0004' }
    });
    throw error;
  }

  if (!response.ok) {
    const apiError = handleApiError(response, responseData, 'MFA登録チケット取得APIの呼び出しエラー');
    logError('MFA登録チケット取得でAPIエラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'POST',
        path: '/api/auth/mfa/enrollment-ticket',
        status: apiError.statusCode
      },
      data: {
        message_id: apiError.errorCode
      }
    });
    throw apiError;
  }

  const ticketUrl = responseData?.data?.ticket_url;
  if (typeof ticketUrl !== 'string' || ticketUrl.length === 0) {
    const error = new Error('E005-0004: レスポンスにticket_urlが含まれていません');
    error.code = 'E005-0004';
    error.statusCode = 500;
    logError('MFA登録チケット取得でレスポンス形式エラーが発生しました', {
      event: 'error',
      category: 'api',
      http: { method: 'POST', path: '/api/auth/mfa/enrollment-ticket', status: 500 },
      data: { message_id: 'E005-0004' }
    });
    throw error;
  }

  return { ticket_url: ticketUrl };
}

/**
 * ログアウト処理
 * ログアウト要求APIを呼び出し、Auth0ログアウトURLを取得してリダイレクト
 *
 * 設計ドキュメント: 06_10_ログアウト処理詳細設計.md
 *
 * @param {string} clientId - システム識別子（Auth0のClient ID）
 * @param {string} [returnTo] - ログアウト後のリダイレクト先（任意）
 * @returns {Promise<void>}
 * @throws {Error} バリデーションエラーまたはAPI呼び出しエラー
 */
async function logout(clientId, returnTo) {
  // 1. パラメータの取得と基本バリデーション

  // clientIdのバリデーション
  const clientIdValidation = validateClientId(clientId);
  if (!clientIdValidation.isValid) {
    const error = new Error(`${clientIdValidation.errorCode}: ${clientIdValidation.errorMessage}`);
    error.code = clientIdValidation.errorCode;
    error.statusCode = clientIdValidation.statusCode;

    // エラーログ出力
    logError('ログアウト処理でバリデーションエラーが発生しました', {
      event: 'error',
      category: 'validation',
      http: {
        method: 'GET',
        path: '/api/auth/logout',
        status: clientIdValidation.statusCode
      },
      data: {
        message_id: clientIdValidation.errorCode,
        clientId: clientId,
        returnTo: returnTo || null
      }
    });

    throw error;
  }

  // returnToのバリデーション（設定されている場合のみ）
  if (returnTo !== undefined && returnTo !== null) {
    const returnToValidation = validateRedirectUri(returnTo);
    if (!returnToValidation.isValid) {
      const error = new Error(`${returnToValidation.errorCode}: ${returnToValidation.errorMessage}`);
      error.code = returnToValidation.errorCode;
      error.statusCode = returnToValidation.statusCode;

      // エラーログ出力
      logError('ログアウト処理でバリデーションエラーが発生しました', {
        event: 'error',
        category: 'validation',
        http: {
          method: 'GET',
          path: '/api/auth/logout',
          status: returnToValidation.statusCode
        },
        data: {
          message_id: returnToValidation.errorCode,
          clientId: clientId,
          returnTo: returnTo
        }
      });

      throw error;
    }
  }

  // ブラウザ環境チェック
  if (typeof window === 'undefined') {
    const error = new Error('E005-0003: ブラウザ環境で実行する必要があります');
    error.code = 'E005-0003';
    error.statusCode = 500;

    logError('ログアウト処理で環境エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'GET',
        path: '/api/auth/logout',
        status: 500
      },
      data: {
        message_id: 'E005-0003',
        clientId: clientId,
        returnTo: returnTo || null
      }
    });

    throw error;
  }

  // API Gateway URLの取得
  const apiGatewayUrl = API_GATEWAY_URL;
  if (!apiGatewayUrl) {
    const error = new Error('E005-0003: 環境変数が設定されていない');
    error.code = 'E005-0003';
    error.statusCode = 500;

    logError('ログアウト処理で環境変数エラーが発生しました', {
      event: 'error',
      category: 'system',
      http: {
        method: 'GET',
        path: '/api/auth/logout',
        status: 500
      },
      data: {
        message_id: 'E005-0003',
        clientId: clientId,
        returnTo: returnTo || null
      }
    });

    throw error;
  }

  // 2. ログアウト要求APIを呼び出し
  const queryParams = new URLSearchParams({
    client_id: clientId
  });

  if (returnTo) {
    queryParams.append('return_to', returnTo);
  }

  const apiUrl = `${apiGatewayUrl}/api/auth/logout?${queryParams.toString()}`;

  let response;
  try {
    response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include' // Cookieを送信するために必要
    });
  } catch (networkError) {
    // ネットワークエラー
    const error = new Error('E006-0004: ネットワークエラーが発生しました');
    error.code = 'E006-0004';
    error.statusCode = 504;
    error.originalError = networkError;

    logError('ログアウト処理でネットワークエラーが発生しました', {
      event: 'error',
      category: 'network',
      http: {
        method: 'GET',
        path: '/api/auth/logout',
        status: 504
      },
      data: {
        message_id: 'E006-0004',
        clientId: clientId,
        returnTo: returnTo || null
      }
    });

    throw error;
  }

  // レスポンスの解析
  let responseData;
  try {
    responseData = await response.json();
  } catch (parseError) {
    const error = new Error('E005-0004: レスポンス解析エラーが発生しました');
    error.code = 'E005-0004';
    error.statusCode = 500;
    error.originalError = parseError;

    logError('ログアウト処理でレスポンス解析エラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/auth/logout',
        status: 500
      },
      data: {
        message_id: 'E005-0004',
        clientId: clientId,
        returnTo: returnTo || null
      }
    });

    throw error;
  }

  // エラーレスポンスの処理
  if (!response.ok) {
    const apiError = handleApiError(response, responseData, 'ログアウト要求APIの呼び出しエラー');

    logError('ログアウト処理でAPIエラーが発生しました', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/auth/logout',
        status: apiError.statusCode
      },
      data: {
        message_id: apiError.code || 'E005-0004',
        clientId: clientId,
        returnTo: returnTo || null
      }
    });

    throw apiError;
  }

  // 3. Auth0ログアウトURLを取得
  if (!responseData.data || !responseData.data.logout_url) {
    const error = new Error('E005-0004: レスポンスデータが不正です');
    error.code = 'E005-0004';
    error.statusCode = 500;

    logError('ログアウト処理でレスポンスデータが不正です', {
      event: 'error',
      category: 'api',
      http: {
        method: 'GET',
        path: '/api/auth/logout',
        status: 500
      },
      data: {
        message_id: 'E005-0004',
        clientId: clientId,
        returnTo: returnTo || null
      }
    });

    throw error;
  }

  const logoutUrl = responseData.data.logout_url;

  // 4. Auth0ログアウトURLにリダイレクト
  // アクセスログ出力
  info('ログアウト処理が正常に完了しました', {
    event: 'logout_success',
    category: 'auth',
    http: {
      method: 'GET',
      path: '/api/auth/logout',
      status: 200
    },
    data: {
      clientId: clientId,
      returnTo: returnTo || null
    }
  });

  if (typeof window !== 'undefined' && window.location) {
    window.location.href = logoutUrl;
  }
}

module.exports = {
  // ログインリダイレクト
  redirectToLogin,

  // コールバック処理
  handleCallback,

  // 認証状態確認
  checkAuthStatus,

  // MFA登録チケット取得
  getMfaEnrollmentTicket,

  // ログアウト
  logout,

  // パスワードリセット
  resetPassword: () => {
    throw new Error('Not implemented');
  }
};
